import { queryOptions } from '@tanstack/react-query'
import { api } from '@/api/client'
import type {
  SocialMedia,
  TeamShow,
  TeamWrite,
  TeamsListQuery,
  TeamsListResponse,
} from '@/api/generated/types'

export type TeamListParams = Pick<
  TeamsListQuery,
  'page' | 'page_size' | 'query' | 'server' | 'inactive' | 'without_members'
>

export type TeamDetail = TeamShow

export const listTeams = (params: TeamListParams, signal?: AbortSignal) =>
  api.get<TeamsListResponse>('/teams', { params, signal }).then((r) => r.data)

export const getTeam = (uuid: string, signal?: AbortSignal) =>
  api.get<TeamShow>(`/teams/${uuid}`, { signal }).then((r) => r.data)

export const createTeam = (body: TeamWrite) =>
  api.post<TeamShow>('/teams', body).then((r) => r.data)

export const updateTeam = (uuid: string, body: TeamWrite) =>
  api.put<TeamShow>(`/teams/${uuid}`, body).then((r) => r.data)

export const deleteTeam = (uuid: string) => api.delete(`/teams/${uuid}`).then(() => undefined)

export const getTeamSocialMedias = (uuid: string, signal?: AbortSignal) =>
  api.get<SocialMedia>(`/teams/${uuid}/social-medias`, { signal }).then((r) => r.data)

export const updateTeamSocialMedias = (uuid: string, body: SocialMedia) =>
  api.put<SocialMedia>(`/teams/${uuid}/social-medias`, body).then((r) => r.data)

export const teamQueries = {
  all: ['teams'] as const,
  /** Prefix for every list query, so a write can refresh lists without touching open details. */
  lists: ['teams', 'list'] as const,
  list: (params: TeamListParams) =>
    queryOptions({
      queryKey: [...teamQueries.lists, params],
      queryFn: ({ signal }) => listTeams(params, signal),
      placeholderData: (previous) => previous,
    }),
  detail: (uuid: string) =>
    queryOptions({
      queryKey: ['teams', 'detail', uuid],
      queryFn: ({ signal }) => getTeam(uuid, signal),
    }),
  socialMedia: (uuid: string) =>
    queryOptions({
      queryKey: ['teams', 'social-medias', uuid],
      queryFn: ({ signal }) => getTeamSocialMedias(uuid, signal),
    }),
}
